import { useEffect, useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { useT } from '../i18n/useT';
import { Icon } from './Icon';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/**
 * Two small, dismissible prompts:
 *  - "New version available → Reload" when the service worker updates.
 *  - "Install markbird as an app" when the browser offers A2HS.
 * Both are opt-in and never block the UI.
 */
export function PwaPrompt() {
  const t = useT();
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({ immediate: true });
  const [installEvt, setInstallEvt] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    const onPrompt = (e: Event) => {
      e.preventDefault();
      setInstallEvt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    const onInstalled = () => setInstallEvt(null);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  return (
    <>
      {needRefresh && (
        <div className="mb-toast mb-toast--top" role="status">
          <span>{t('pwa.updateBody')}</span>
          <button className="mb-btn mb-btn-primary" onClick={() => void updateServiceWorker(true)}>
            {t('pwa.reload')}
          </button>
          <button
            className="mb-toast-close"
            aria-label={t('pwa.dismiss')}
            onClick={() => setNeedRefresh(false)}
          >
            <Icon name="x" size={13} />
          </button>
        </div>
      )}
      {installEvt && (
        <div className="mb-toast mb-toast--corner" role="status">
          <span>{t('pwa.installBody')}</span>
          <button
            className="mb-btn mb-btn-primary"
            onClick={async () => {
              await installEvt.prompt();
              setInstallEvt(null);
            }}
          >
            {t('pwa.install')}
          </button>
          <button
            className="mb-toast-close"
            aria-label={t('pwa.dismiss')}
            onClick={() => setInstallEvt(null)}
          >
            <Icon name="x" size={13} />
          </button>
        </div>
      )}
    </>
  );
}
